import { AuthUIActions, SecurityContextActions } from '@brightlayer-ui/react-auth-workflow';
import { LocalStorage } from '../store/local-storage';
import { CommonStrings, ESstrings } from '../localisation/strings.en';
import moment from 'moment';
import { AuthService } from '../APIService/AuthService';

type AuthUIActionsFunction = () => AuthUIActions;
type AuthUIActionsWithSecurity = (securityHelper: SecurityContextActions) => AuthUIActionsFunction;

/** Extract the first role name from the getUserDetail response. */
function extractRoleName(roleResp: any): string {
    return (
        roleResp?.data?.data?.role_name ||
        roleResp?.data?.role_name ||
        roleResp?.data?.data?.role?.name ||
        ''
    );
}

/** Returns true when the role is allowed to access the application. */
function isAllowedRole(roleName: string): boolean {
    const normalized = roleName.trim().toLowerCase();
    return normalized === 'eaton admin' || normalized === 'adopter admin';
}

/**
 * Implementation of [[AuthUIActions]].
 *
 * Authentication Actions to be performed based on the user's UI actions. The application will create
 * appropriate actions (often api calls, local network storage, credential updates, etc) and update
 * the global security state based on the actionable needs of the user.
 */
export const ProjectAuthUIActions: AuthUIActionsWithSecurity = (securityHelper) => (): AuthUIActions => ({
    /**
     * Initialize the application security state. This will involve reading any local storage,
     * validating existing credentials (token expiration, for example). At the end of validation,
     * the [[SecurityContextActions]] should be called with either:
     * [[onUserAuthenticated]] (which will present the application), or
     * [[onUserNotAuthenticated]] (which will present the Auth UI).
     *
     * Note: Until this method returns, the applications Splash screen will be presented.
     *
     * @returns Should always resolve. Never throw.
     */
    initiateSecurity: async (): Promise<void> => {
        let authData;

        try {
            authData = await LocalStorage.readAuthData();
        } catch {
            // Restoring session failed – fall through to unauthenticated state
        }

        const path = window.location.pathname;
        if (authData?.email !== undefined && !path.includes('custom-invite-route')) {
            securityHelper.onUserAuthenticated({
                email: authData.email,
                userId: authData.userId ?? '',
                rememberMe: authData.rememberMeData.rememberMe,
            });
        } else {
            const rememberMeEmail = authData?.rememberMeData.rememberMe ? authData.rememberMeData.user : undefined;
            securityHelper.onUserNotAuthenticated(false, rememberMeEmail);
        }
    },

    /**
     * The user wants to log into the application. Perform a login with the user's credentials.
     *
     * @param email Email address the user entered into the UI.
     * @param password Password the user entered into the UI.
     * @param rememberMe Indicates whether the user's email should be remembered on success.
     *
     * @returns Resolve if credentials are valid, otherwise reject.
     */
    logIn: async (email: string, password: string, rememberMe: boolean): Promise<void> => {
        const params = {
            username: email,
            password,
            options: {
                multiOptionalFactorEnroll: false,
                warnBeforePasswordExpired: false,
            },
        };

        const loginResp: any = await AuthService.login(params);

        if (!loginResp.success) {
            throw new Error(loginResp?.errorResponse?.errorDescription ?? 'Your username/password combination is not recognized.');
        }

        LocalStorage.userData(loginResp.data.data);
        LocalStorage.saveAuthCredentials(email, email);
        LocalStorage.saveRememberMeData(email, rememberMe);
        LocalStorage.setLoginTimestamp(moment().unix());

        const userId = loginResp?.data?.data?.userId || loginResp?.data?.data?.id || email;
        const roleResp: any = await AuthService.getUserDetail(userId);
        const roleName = extractRoleName(roleResp);

        if (roleResp?.success && isAllowedRole(roleName)) {
            LocalStorage.setUserRole(roleName);
            securityHelper.onUserAuthenticated({ email, userId: email, rememberMe });
        } else {
            LocalStorage.clearAuthCredentials();
            localStorage.removeItem(ESstrings.USER_PROFILE);
            throw new Error('Only Eaton Admin has access to this application.');
        }
    },

    /**
     * The user has forgotten their password and wants help.
     *
     * @param email Email address the user entered into the UI.
     *
     * @returns Resolve if email sending was successful, otherwise reject.
     */
    forgotPassword: async (email: string): Promise<void> => {
        const setPassResp: any = await AuthService.requestPasswordReset(email);
        if (setPassResp.success) {
            return;
        }
        const errMsg =
            setPassResp.errorResponse?.errorDescription ?? CommonStrings.WentWrong;
        throw new Error(errMsg);
    },

    /**
     * The user has tapped on an email with a password reset link.
     * The application should take the password reset code and then verify that it is still valid.
     *
     * @param code Password reset code from a reset password link.
     * @param email Email if it was passed from the reset link.
     *
     * @returns Resolve if code is valid, otherwise reject.
     */
    verifyResetCode: async (_code: string, _email?: string): Promise<void> => {
        return;
    },

    /**
     * A user who has previously used "forgotPassword" now has a valid password reset code
     * and has entered a new password.
     *
     * @param code Password reset code from a link.
     * @param password New Password the user entered into the UI.
     * @param email Email if it was passed from the reset link.
     *
     * @returns Resolve if successful, otherwise reject with an error message.
     */
    setPassword: async (code: string, password: string, email?: string): Promise<void> => {
        if (!email) {
            throw new Error('The password reset link is invalid or missing required information.');
        }
        const params = {
            resetcode: code,
            newpassword: password,
        };

        const resetPassResp: any = await AuthService.resetPassword(email, params);

        if (!resetPassResp.success) {
            throw new Error(resetPassResp.errorResponse?.errorDescription ?? CommonStrings.WentWrong);
        }
    },

    /**
     * An authenticated user wants to change their password.
     * Upon success the user is logged out so they can re-authenticate with the new password.
     *
     * @param oldPassword The user's current password as entered into the UI.
     * @param newPassword The user's new password as entered into the UI.
     *
     * @returns Resolve if successful, otherwise reject with an error message.
     */
    changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
        const params = {
            currentpassword: oldPassword,
            newpassword: newPassword,
        };

        const changePassResp: any = await AuthService.changePassword(params);

        if (changePassResp.success) {
            // Delay logout slightly to allow the success UI to render
            setTimeout(() => {
                LocalStorage.clearAuthCredentials();
                localStorage.removeItem(ESstrings.USER_PROFILE);
                securityHelper.onUserNotAuthenticated();
            }, 2500);
            return;
        }

        // 401 is handled globally by the authErrorInterceptor in http-client-instance.ts
        // which will clear credentials and redirect to login. Surface a friendly message.
        const errMsg =
            changePassResp.errorResponse?.errorDescription ?? CommonStrings.WentWrong;
        throw new Error(errMsg);
    },
});

